"use client";
import React from "react";
import { motion } from "motion/react";
import Link from "next/link";
import { HugeiconsIcon } from "@hugeicons/react";
import { ArrowUpRight01Icon, Mail01Icon } from "@hugeicons/core-free-icons";
import { ProjectType } from "@/components/utils/types";

interface CallToActionProps {
  project: ProjectType;
}

export const CallToAction = ({ project }: CallToActionProps) => {
  return (
    <section className="relative w-full py-32 md:py-48 px-6 md:px-20 overflow-hidden flex flex-col items-center justify-center">
      {/* Background Gradients */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-sky-500/10 dark:bg-sky-500/5 rounded-full blur-[100px]" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-500/10 dark:bg-blue-500/5 rounded-full blur-[80px]" />
      </div>

      <div className="relative z-10 max-w-4xl mx-auto text-center flex flex-col items-center gap-8 md:gap-10">
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ type: "spring", stiffness: 100, damping: 20 }}
          className="font-recoleta text-5xl md:text-7xl lg:text-8xl text-black dark:text-white leading-tight"
        >
          Ready to build your{" "}
          <span className="bg-linear-to-r from-sky-500 to-blue-600 bg-clip-text text-transparent">
            Vision?
          </span>
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{
            type: "spring",
            stiffness: 100,
            damping: 20,
            delay: 0.2,
          }}
          className="font-outfit text-xl md:text-2xl text-black/60 dark:text-white/60 max-w-2xl"
        >
          I specialize in building high-performance, scalable web applications
          like {project.name}. Let's collaborate to turn your innovative ideas
          into reality.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{
            type: "spring",
            stiffness: 100,
            damping: 20,
            delay: 0.4,
          }}
          className="flex flex-col md:flex-row items-center gap-4 md:gap-6 mt-4"
        >
          {/* Visit App Button */}
          <Link
            href={project.url}
            target="_blank"
          >
            <motion.button
              whileHover={{
                scale: 1.05,
                transition: { type: "spring", stiffness: 400, damping: 15 },
              }}
              whileTap={{
                scale: 0.95,
                transition: { type: "spring", stiffness: 400, damping: 15 },
              }}
              className="group relative px-8 py-4 bg-black dark:bg-white text-white dark:text-black rounded-full font-outfit text-lg font-medium tracking-wide overflow-hidden shadow-xl hover:shadow-sky-500/20 transition-all duration-300"
            >
              <span className="relative z-10 flex items-center gap-2">
                Visit App
                <HugeiconsIcon icon={ArrowUpRight01Icon} className="w-5 h-5 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
              </span>
              <div className="absolute inset-0 bg-linear-to-r from-sky-600 to-blue-600 dark:from-sky-300 dark:to-blue-400 opacity-0 group-hover:opacity-10 dark:group-hover:opacity-20 transition-opacity duration-300" />
            </motion.button>
          </Link>

          {/* Contact Button */}
          <Link href="mailto:jigneshbaria1777@gmail.com">
            <motion.button
              whileHover={{
                scale: 1.05,
                transition: { type: "spring", stiffness: 400, damping: 15 },
              }}
              whileTap={{
                scale: 0.95,
                transition: { type: "spring", stiffness: 400, damping: 15 },
              }}
              className="group px-8 py-4 bg-transparent border-2 border-black/10 dark:border-white/10 text-black dark:text-white rounded-full font-outfit text-lg font-medium tracking-wide hover:border-black/30 dark:hover:border-white/30 hover:bg-black/5 dark:hover:bg-white/5 transition-all duration-300 flex items-center gap-2"
            >
              Contact Me
              <HugeiconsIcon icon={Mail01Icon} className="w-5 h-5 transition-transform group-hover:rotate-12" />
            </motion.button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};
